import chroma from "chroma-js";

export const IDEAL_CONTRAST_RATIO = 4.5;
export const WHITE = "#ffffff";
export const BLACK = "#000000";

export const isBright = (colour: string) => chroma(colour).luminance() > 0.5;

export const isLowContrast = (colour: string, colour2: string) =>
  chroma.contrast(colour, colour2) < IDEAL_CONTRAST_RATIO;

export const getPrimaryColorSpectrum = (
  color: string,
  light: string,
  dark: string
) => {
  const scale = chroma.scale([light, chroma(color), dark]).domain([0, 800]);

  return {
    colorPrimary100: scale(33).hex(),
    colorPrimary200: scale(70).hex(),
    colorPrimary300: scale(113).hex(),
    colorPrimary400: color,
    colorPrimary500: scale(519).hex(),
  };
};

export const getGraysColorSpectrum = (light: string, dark: string) => {
  const scale = chroma.scale([light, dark]).domain([0, 1000]);

  return {
    colorGray100: scale(50).hex(),
    colorGray200: scale(63).hex(),
    colorGray300: scale(122).hex(),
    colorGray400: scale(311).hex(),
    colorGray500: scale(487).hex(),
    colorGray600: scale(648).hex(),
    colorGray650: scale(707).hex(),
    colorGray700: scale(834).hex(),
    colorGray800: scale(1000).hex(),
    colorGray900: isBright(dark)
      ? scale(1000).brighten(0.5).hex()
      : scale(1000).darken(0.5).hex(),
    colorBlack: isBright(dark)
      ? scale(1000).brighten(1.6).hex()
      : scale(1000).darken(1.6).hex(),
    colorWhite: light,
  };
};

export const getEventBodyBgColor = (light: string, dark: string) => {
  if (light === WHITE) {
    return light;
  }

  return isBright(dark)
    ? chroma(light).brighten(0.2).hex()
    : chroma(light).darken(0.05).hex();
};

export const getPrimaryButtonColor = (primaryColour: string) => {
  return isLowContrast(primaryColour, WHITE) ? BLACK : WHITE;
};

export const getFullTheme = ({
  primary,
  light,
  dark,
  overrides,
}: {
  primary: string;
  light: string;
  dark: string;
  overrides?: Record<string, string>;
}): Partial<Theme> => ({
  ...getPrimaryColorSpectrum(primary, light, dark),
  ...getGraysColorSpectrum(light, dark),
  colorButtonPrimaryText: getPrimaryButtonColor(primary),
  colorEventBodyBackground: getEventBodyBgColor(light, dark),
  colorTabpanelBackground: light,
  ...overrides,
});

export type Theme = {
  colorPrimary100: string;
  colorPrimary200: string;
  colorPrimary300: string;
  colorPrimary400: string;
  colorPrimary500: string;
  colorGray100: string;
  colorGray200: string;
  colorGray300: string;
  colorGray400: string;
  colorGray500: string;
  colorGray600: string;
  colorGray650: string;
  colorGray700: string;
  colorGray800: string;
  colorGray900: string;
  colorBlack: string;
  colorWhite: string;
  colorTextHeadlineLarge: string;
  colorTextHeadlineMedium: string;
  colorTextHeadlineSmall: string;
  colorTextLabel: string;
  colorTextLinks: string;
  colorTextLinkHover: string;
  colorButtonPrimaryText: string;
  colorButtonPrimaryBackground: string;
  colorButtonPrimaryTextHover: string;
  colorButtonPrimaryBackgroundHover: string;
  colorButtonSecondaryText: string;
  colorButtonSecondaryBorder: string;
  colorButtonSecondaryBackground: string;
  colorButtonSecondaryTextHover: string;
  colorButtonSecondaryBackgroundHover: string;
  colorInputText: string;
  colorInputBackground: string;
  colorInputBorder: string;
  colorHeaderBackground: string;
  colorSidenavBackground: string;
  colorEventBodyBackground: string;
  colorVideoBodyBackground: string;
  colorVideoBodyPlaceholderText: string;
  colorTabpanelBackground: string;
  colorSidenavIconDefault: string;
  colorSidenavIconDefaultHover: string;
  colorSidenavIconDefaultBackground: string;
  colorSidenavIconDefaultBackgroundHover: string;
  colorSidenavIconDefaultText: string;
  colorSidenavIconDefaultTextHover: string;
  colorSidenavIconActive: string;
  colorSidenavIconActiveHover: string;
  colorSidenavIconActiveBackground: string;
  colorSidepanelTabDefault: string;
  colorSidepanelTabDefaultHover: string;
  colorSidepanelTabDefaultBackground: string;
  colorSidepanelTabDefaultBackgroundHover: string;
  colorSidepanelTabActive: string;
  colorSidepanelTabActiveBackground: string;
  colorMenuBackground: string;
  colorMenuBackgroundHover: string;
  colorMenuText: string;
  colorMenuBorder: string;
  colorMenuItemHighlight: string;
  colorModalBorder: string;
  colorModalHeaderBackground: string;
  colorModalBodyBackground: string;
  colorModalFooterBackground: string;
  colorModalOverlayBackground: string;
  colorCardBackground: string;
  colorCardBorder: string;
  colorIconFill: string;
  colorIconBackground: string;
  colorTooltip: string;
  colorTooltipBackground: string;
  colorMessageBackground: string;
  colorMessageHighlightBackground: string;
  colorHappeningNowCardBackground: string;
  colorHappeningNowCardText: string;
  colorScheduleBackground: string;
  colorScheduleTitle: string;
  colorScheduleDescription: string;
  colorScheduleSegment: string;
  colorScheduleHighlightBackground: string;
  colorScheduleHighlightTitle: string;
  colorScheduleHighlightDescription: string;
  colorScheduleHighlightSegment: string;
  buttonBorderRadius: string;
  inputBorderRadius: string;
  customCSS: string;
  advancedMode: boolean;
  darkMode: boolean;
  themeId?: string;
};
